import * as _ from 'lodash-es';
import type { Delta, Range } from 'quill/core';
import Quill from 'quill/core';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAppDispatch } from '../app/redux/useAppSelector';
import { useQuill } from '../editor/QuillContext';
import { getRangeSaver } from '../editor/RangeSaver';
import { EditorActions } from '../editor/editorSlice';
import transformRange from '../editor/transformRange';

const PendingMagneticWallItemContext = React.createContext<{
  pendingCommentThreadRange: Range | null;
  pendingAnnotationRange: Range | null;
  setPendingCommentThreadRange: (range: Range | null) => void;
  setPendingAnnotationRange: (range: Range | null) => void;
  createPendingCommentThread: () => void;
  createPendingAnnotation: () => void;
}>({
  pendingCommentThreadRange: null,
  pendingAnnotationRange: null,
  setPendingCommentThreadRange: () => {},
  setPendingAnnotationRange: () => {},
  createPendingCommentThread: () => {},
  createPendingAnnotation: () => {},
});

export const COMMENT_THREAD_CREATION_ID = 'create-comment-thread';
export const ANNOTATION_CREATION_ID = 'create-annotation';

const PendingMagneticWallItemProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const dispatch = useAppDispatch();

  const { quill } = useQuill();
  const [pendingCommentThreadRange, setPendingCommentThreadRange] =
    useState<Range | null>(null);
  const [pendingAnnotationRange, setPendingAnnotationRange] =
    useState<Range | null>(null);

  const createPendingCommentThread = useCallback(() => {
    if (!quill) return;

    setPendingCommentThreadRange(getRangeSaver(quill).getLast());
    dispatch(
      EditorActions.focusMagneticWall({
        id: COMMENT_THREAD_CREATION_ID,
        type: 'comment',
        isActive: true,
        hasExpandIntent: false,
      }),
    );
  }, [quill, dispatch]);

  const createPendingAnnotation = useCallback(() => {
    if (!quill) return;

    setPendingAnnotationRange(getRangeSaver(quill).getLast());
    dispatch(
      EditorActions.focusMagneticWall({
        id: ANNOTATION_CREATION_ID,
        type: 'annotation',
        isActive: true,
        hasExpandIntent: false,
      }),
    );
  }, [quill, dispatch]);

  useEffect(() => {
    if (!quill) return undefined;
    const handleTextChange = (delta: Delta) => {
      setPendingCommentThreadRange((range) => {
        const result = range ? transformRange(range, delta) : null;
        if (_.isEqual(result, range)) return range;
        return result;
      });
      setPendingAnnotationRange((range) => {
        const result = range ? transformRange(range, delta) : null;
        if (_.isEqual(result, range)) return range;
        return result;
      });
    };

    quill.on(Quill.events.TEXT_CHANGE, handleTextChange);
    return () => {
      quill.off(Quill.events.TEXT_CHANGE, handleTextChange);
    };
  }, [quill]);

  const contextValue = useMemo(
    () => ({
      pendingCommentThreadRange,
      pendingAnnotationRange,
      setPendingCommentThreadRange,
      setPendingAnnotationRange,
      createPendingCommentThread,
      createPendingAnnotation,
    }),
    [
      pendingCommentThreadRange,
      pendingAnnotationRange,
      setPendingCommentThreadRange,
      setPendingAnnotationRange,
      createPendingCommentThread,
      createPendingAnnotation,
    ],
  );

  return (
    <PendingMagneticWallItemContext.Provider value={contextValue}>
      {children}
    </PendingMagneticWallItemContext.Provider>
  );
};

export const usePendingMagneticWallItem = () =>
  useContext(PendingMagneticWallItemContext);

export default PendingMagneticWallItemProvider;
